/**
 * Report-specific TypeScript interfaces for the Reports page
 */

// Default grid rates for calculations when no specific rate is available
// These are used as fallbacks in comparison views that aggregate multiple entities
// NOTE: These should match the system default rate in energy_rates table ($0.18 import, $0.08 sell)
export const GRID_IMPORT_RATE_PER_KWH = 0.18; // $0.18/kWh grid import rate (system default)
export const GRID_SELL_RATE_PER_KWH = 0.08; // $0.08/kWh grid buyback rate (NEM 3.0 default)
export const SOLAR_VALUE_PER_KWH = 0.18; // $0.18/kWh value of solar self-consumption (offset import)

export type DatePreset =
  | 'today'
  | 'yesterday'
  | 'last-7-days'
  | 'last-30-days'
  | 'last-90-days'
  | 'this-month'
  | 'last-month'
  | 'this-quarter'
  | 'last-quarter'
  | 'q1'
  | 'q2'
  | 'q3'
  | 'q4'
  | 'this-year'
  | 'last-year'
  | 'custom';

export interface DateRange {
  start: Date | null;
  end: Date | null;
}

export interface ReportFilters {
  datePreset: DatePreset;
  customDateRange: DateRange;
  companyIds: string[];
  groupIds: string[];
}

export interface ReportRow {
  unitId: string;
  unitCode: string;
  companyName: string;
  groupName: string;
  /** Unit status - aligned with Unit.status from src/types/index.ts */
  status: 'online' | 'offline' | 'idle' | 'unknown';
  batteryLevel: number;
  solarOutput: number;
  gridEnergy: number;
  offGridPercentage: number;
  co2Reduced: number;
  operatingHours: number;
  lastSeen: string;
}

export interface ReportSummary {
  totalUnits: number;
  totalEnergyGenerated: number;
  totalOffGridPercentage: number;
  totalCO2Reduced: number;
  averageBatteryLevel: number;
  onlineUnits: number;
  offlineUnits: number;
  idleUnits: number;
  modelCounts: Record<string, number>;
  /** Unit ids behind each Unit Status count (Idle/Charging includes unknown) — click-to-focus */
  statusUnitIds?: { online: string[]; offline: string[]; idle: string[] };
}

export interface ReportData {
  rows: ReportRow[];
  summary: ReportSummary;
  generatedAt: string;
  filters: {
    dateRange: string;
    companies: string[];
    groups: string[];
  };
}

export interface DatePresetOption {
  value: DatePreset;
  label: string;
  shortLabel: string;
  group: 'quick' | 'monthly' | 'quarterly' | 'yearly' | 'custom';
}

export const DATE_PRESET_OPTIONS: DatePresetOption[] = [
  // Quick ranges
  { value: 'today', label: 'Today', shortLabel: 'Today', group: 'quick' },
  { value: 'yesterday', label: 'Yesterday', shortLabel: 'Yesterday', group: 'quick' },
  { value: 'last-7-days', label: 'Last 7 Days', shortLabel: '7 Days', group: 'quick' },
  { value: 'last-30-days', label: 'Last 30 Days', shortLabel: '30 Days', group: 'quick' },
  { value: 'last-90-days', label: 'Last 90 Days', shortLabel: '90 Days', group: 'quick' },
  // Monthly
  { value: 'this-month', label: 'This Month', shortLabel: 'This Mo.', group: 'monthly' },
  { value: 'last-month', label: 'Last Month', shortLabel: 'Last Mo.', group: 'monthly' },
  // Quarterly
  { value: 'this-quarter', label: 'This Quarter', shortLabel: 'This Qtr', group: 'quarterly' },
  { value: 'last-quarter', label: 'Last Quarter', shortLabel: 'Last Qtr', group: 'quarterly' },
  { value: 'q1', label: 'Q1 (Jan-Mar)', shortLabel: 'Q1', group: 'quarterly' },
  { value: 'q2', label: 'Q2 (Apr-Jun)', shortLabel: 'Q2', group: 'quarterly' },
  { value: 'q3', label: 'Q3 (Jul-Sep)', shortLabel: 'Q3', group: 'quarterly' },
  { value: 'q4', label: 'Q4 (Oct-Dec)', shortLabel: 'Q4', group: 'quarterly' },
  // Yearly
  { value: 'this-year', label: 'This Year', shortLabel: 'This Yr', group: 'yearly' },
  { value: 'last-year', label: 'Last Year', shortLabel: 'Last Yr', group: 'yearly' },
  // Custom
  { value: 'custom', label: 'Custom Range', shortLabel: 'Custom', group: 'custom' },
];

/**
 * Calculate actual date range from a preset
 *
 * All ranges are normalized to full inclusive days:
 * - start: beginning of day (00:00:00.000)
 * - end: end of day (23:59:59.999)
 * This ensures consistent date range comparisons regardless of when the query is made.
 */
export function getDateRangeFromPreset(preset: DatePreset, year?: number, now: Date = new Date()): DateRange {
  const currentYear = year || now.getFullYear();

  switch (preset) {
    case 'today':
      return {
        start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0),
        end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999),
      };
    case 'yesterday': {
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      return {
        start: new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 0, 0, 0, 0),
        end: new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 23, 59, 59, 999),
      };
    }
    case 'last-7-days': {
      // "Last 7 days" = exactly 7 inclusive calendar days ending today, so the
      // start is today − (7 − 1). Matches the authoritative server resolver
      // (api/services/reportDateRange.js daysAgo(6)); see ISS-068.
      const start = new Date(now);
      start.setDate(start.getDate() - 6);
      start.setHours(0, 0, 0, 0);
      // End: today at 23:59:59.999 (inclusive full day range)
      const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      return { start, end };
    }
    case 'last-30-days': {
      // 30 inclusive days ending today → start = today − 29 (ISS-068).
      const start = new Date(now);
      start.setDate(start.getDate() - 29);
      start.setHours(0, 0, 0, 0);
      // End: today at 23:59:59.999 (inclusive full day range)
      const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      return { start, end };
    }
    case 'last-90-days': {
      // 90 inclusive days ending today → start = today − 89 (ISS-068).
      const start = new Date(now);
      start.setDate(start.getDate() - 89);
      start.setHours(0, 0, 0, 0);
      // End: today at 23:59:59.999 (inclusive full day range)
      const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      return { start, end };
    }
    case 'this-month':
      return {
        start: new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0),
        end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999),
      };
    case 'last-month': {
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
      const lastDayOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
      return { start: lastMonth, end: lastDayOfLastMonth };
    }
    case 'this-quarter': {
      const quarter = Math.floor(now.getMonth() / 3);
      return {
        start: new Date(now.getFullYear(), quarter * 3, 1, 0, 0, 0, 0),
        end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999),
      };
    }
    case 'last-quarter': {
      const currentQuarter = Math.floor(now.getMonth() / 3);
      const lastQuarter = currentQuarter === 0 ? 3 : currentQuarter - 1;
      const lastQuarterYear = currentQuarter === 0 ? now.getFullYear() - 1 : now.getFullYear();
      return {
        start: new Date(lastQuarterYear, lastQuarter * 3, 1, 0, 0, 0, 0),
        end: new Date(lastQuarterYear, lastQuarter * 3 + 3, 0, 23, 59, 59, 999),
      };
    }
    case 'q1':
      return {
        start: new Date(currentYear, 0, 1, 0, 0, 0, 0),
        end: new Date(currentYear, 2, 31, 23, 59, 59, 999),
      };
    case 'q2':
      return {
        start: new Date(currentYear, 3, 1, 0, 0, 0, 0),
        end: new Date(currentYear, 5, 30, 23, 59, 59, 999),
      };
    case 'q3':
      return {
        start: new Date(currentYear, 6, 1, 0, 0, 0, 0),
        end: new Date(currentYear, 8, 30, 23, 59, 59, 999),
      };
    case 'q4':
      return {
        start: new Date(currentYear, 9, 1, 0, 0, 0, 0),
        end: new Date(currentYear, 11, 31, 23, 59, 59, 999),
      };
    case 'this-year':
      return {
        start: new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0),
        end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999),
      };
    case 'last-year':
      return {
        start: new Date(now.getFullYear() - 1, 0, 1, 0, 0, 0, 0),
        end: new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59, 999),
      };
    case 'custom':
    default:
      return { start: null, end: null };
  }
}

/**
 * Format date range for display
 */
export function formatDateRange(range: DateRange): string {
  if (!range.start || !range.end) return 'Select dates';

  const formatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return `${formatter.format(range.start)} - ${formatter.format(range.end)}`;
}

// ============================================================================
// Production Analytics Types (for sustainability department heads)
// ============================================================================

/**
 * Time granularity for data aggregation
 */
export type TimeGranularity = 'daily' | 'weekly' | 'monthly';

/**
 * Production-level time series data point
 */
export interface GroupTimeSeriesPoint {
  date: string;              // ISO date or period start
  periodLabel: string;       // "Nov 15", "Week 3", "November"
  solarKwh: number;
  gridKwh: number;
  gridExportKwh: number;     // Grid export (kWh)
  generatorKwh: number;      // Generator usage (kWh)
  offGridPercent: number;
  co2Reduced: number;        // kg
  costSaved: number;         // $ solar, each unit-day at its configured import rate
  gridSellRevenue: number;   // $ from grid exports
  /** $ grid import at each unit-day's configured import rate. Absent until the
   *  database has migration 20261027230000 (F03 part 4). */
  gridCost?: number;
  unitsActive: number;
}

/**
 * Available metrics for chart display
 */
export type MetricKey =
  | 'solarKwh'
  | 'gridKwh'
  | 'offGridPercent'
  | 'co2Reduced'
  | 'costSaved'
  | 'gridSellRevenue';

/**
 * Metric toggle configuration for chart controls
 */
export interface MetricToggle {
  key: MetricKey;
  label: string;
  color: string;
  enabled: boolean;
  unit: string;                           // 'kWh', '%', 'kg', '$'
  yAxisGroup: 'absolute' | 'percent';     // For dual-axis chart
}

/**
 * View mode for report display
 */
export type ReportViewMode = 'charts' | 'summary' | 'compare';

/**
 * Production analytics filter state
 */
export interface GroupAnalyticsFilters {
  groupIds: string[];
  modelCodes: string[];
  granularity: TimeGranularity;
  periodCount: number;
  useFullGroup: boolean;
  enabledMetrics: MetricKey[];
  showCharts: boolean;
  showStats: boolean;
  showUnitDetails: boolean;
  viewMode: ReportViewMode;
}

/**
 * Statistics summary for a group period
 */
export interface GroupStatsSummary {
  // Core metrics
  avgOffGrid: number;
  /** null = no measured range peak available (renders N/A) */
  peakSolarKw: number | null;
  totalCO2: number;
  totalCostSaved: number;
  totalSolarKwh: number;
  totalGridKwh: number;
  bestPeriod: { label: string; value: number; metric: string };
  worstPeriod: { label: string; value: number; metric: string };
  trendVsPrior: number;      // % change vs previous equivalent period
  // Enhanced insights (Phase 0)
  dieselGallonsReduced: number;
  treesEquivalent: number;
  /** null = no measured generator runtime source (renders N/A) */
  genHoursReduced: number | null;
  dailyAvgSolarKwh: number;
  daysInPeriod: number;
  priorPeriodComparison: {
    // null = no real prior-period fetch yet (chips hidden)
    solarKwhDelta: number | null;    // % change
    offGridDelta: number | null;     // % change
    co2Delta: number | null;         // % change
  };

  // NEW aggregated energy metrics for Reports (6 new totals) - optional as may not be available
  totalLoadKwh?: number;              // Sum of all load consumption
  totalBatteryChargeKwh?: number;     // Sum of energy into batteries
  totalBatteryDischargeKwh?: number;  // Sum of energy from batteries
  totalGridImportKwh?: number;        // Sum of grid imports
  totalGridExportKwh?: number;        // Sum of grid exports
  totalGeneratorKwh?: number;         // Sum of generator usage
  totalCo2EmissionKg?: number;        // Total CO2 in kg (converted from metric tons)
  avgBatterySoc?: number;             // Average battery SOC across all units

  // Grid sell revenue and profit metrics
  gridSellRevenue?: number;           // totalGridExportKwh * GRID_SELL_RATE_PER_KWH
  totalProfit?: number;               // totalCostSaved + gridSellRevenue
}

/**
 * Station (unit) metrics row for Reports Charts View table
 * All cumulative energy metrics for a single unit
 */
export interface StationMetricsRow {
  unitId: string;
  unitCode: string;                  // Station Name
  /** null: no production, or a unit that has moved on to another company (F04: its group is theirs). */
  groupName: string | null;
  loadKwh: number;                   // Load (kWh)
  /** SOC (%). null for a unit that has moved on to another company (F04: the live battery is theirs). */
  batteryLevel: number | null;
  batteryChargeKwh: number;          // Battery Charge (kWh)
  batteryDischargeKwh: number;       // Battery Discharge (kWh)
  pvKwh: number;                     // PV (kWh) - renamed from energyCreated
  gridImportKwh: number;             // Grid Import (kWh)
  gridExportKwh: number;             // Grid Export (kWh)
  generatorKwh: number;              // Generator (kWh)
  co2EmissionKg: number;             // CO2 Emission (kg)
}

/**
 * Station metrics table data with pagination info
 */
export interface StationMetricsTableData {
  rows: StationMetricsRow[];
  totals: {
    loadKwh: number;
    avgBatterySoc: number;
    batteryChargeKwh: number;
    batteryDischargeKwh: number;
    pvKwh: number;
    gridImportKwh: number;
    gridExportKwh: number;
    generatorKwh: number;
    co2EmissionKg: number;
  };
  totalUnits: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Energy source breakdown for donut chart
 * Note: Battery is not shown separately since Solar → Battery → Load is one path
 * Battery is intermediary storage, not a separate energy source
 */
export interface EnergyMixBreakdown {
  solarKwh: number;
  gridKwh: number;
  generatorKwh: number;
  solarPercent: number;
  gridPercent: number;
  generatorPercent: number;
}

/**
 * Rate information used for report calculations
 */
export interface ReportRateInfo {
  rateName: string;
  gridImportRate: number;
  gridSellRate: number;
  dieselCostPerGallon: number;
  utilityProvider?: string;
  nemVersion?: string;
  rateSource: string;
  sourceName: string;
}

/**
 * Complete group analytics data
 */
export interface GroupAnalyticsData {
  groupName: string;
  groupId: string | 'all';
  dateRange: { start: string; end: string };
  granularity: TimeGranularity;
  timeSeries: GroupTimeSeriesPoint[];
  stats: GroupStatsSummary;
  energyMix: EnergyMixBreakdown;
  unitsInGroup: number;
  /** Rate info used for calculations (optional - defaults to system rate if not provided) */
  effectiveRate?: ReportRateInfo;
}

/**
 * Default metric toggle configuration
 */
export const DEFAULT_METRIC_TOGGLES: MetricToggle[] = [
  { key: 'solarKwh', label: 'Solar Output', color: '#8FB560', enabled: true, unit: 'kWh', yAxisGroup: 'absolute' },
  { key: 'offGridPercent', label: 'Off-Grid %', color: '#10b981', enabled: true, unit: '%', yAxisGroup: 'percent' },
  { key: 'co2Reduced', label: 'CO₂ Reduced', color: '#22c55e', enabled: true, unit: 'kg', yAxisGroup: 'absolute' },
  { key: 'gridKwh', label: 'Grid Usage', color: '#6b7280', enabled: false, unit: 'kWh', yAxisGroup: 'absolute' },
  { key: 'gridSellRevenue', label: 'Grid Sell', color: '#3b82f6', enabled: false, unit: '$', yAxisGroup: 'absolute' },
  { key: 'costSaved', label: 'Cost Savings', color: '#f59e0b', enabled: false, unit: '$', yAxisGroup: 'absolute' },
];

/**
 * Default analytics filters
 */
export const DEFAULT_ANALYTICS_FILTERS: GroupAnalyticsFilters = {
  groupIds: [],
  modelCodes: [],
  granularity: 'weekly',
  periodCount: 4,
  useFullGroup: false,
  enabledMetrics: ['solarKwh', 'offGridPercent', 'co2Reduced'],
  showCharts: true,
  showStats: true,
  showUnitDetails: false,
  viewMode: 'charts',
};

// ============================================================================
// Compare Analytics Types (for side-by-side production comparison)
// ============================================================================

/**
 * Section types for Compare view - each section shows same metric across all comparison slots
 */
export type CompareSection = 'info' | 'kpis' | 'chart' | 'stats' | 'energyMix' | 'gridRevenue';

/**
 * Compare section configuration for UI
 */
export const COMPARE_SECTIONS: { value: CompareSection; label: string }[] = [
  { value: 'info', label: 'Info' },
  { value: 'kpis', label: 'KPIs' },
  { value: 'chart', label: 'Chart' },
  { value: 'stats', label: 'Stats' },
  { value: 'energyMix', label: 'Energy Mix' },
  { value: 'gridRevenue', label: 'Grid Revenue' },
];

/**
 * Slot colors for up to 4 comparison slots
 */
export const COMPARE_SLOT_COLORS = [
  '#8FB560',  // Hollywood Trucks green
  '#60B5A7',  // Teal
  '#B57E60',  // Warm orange
  '#7A60B5',  // Purple
] as const;

/**
 * Saved date settings for restoring after "Use Production Dates" is disabled
 */
export interface PreviousDateSettings {
  datePreset: DatePreset;
  customDateRange: { start: Date | null; end: Date | null };
  granularity: TimeGranularity;
}

/**
 * A single comparison slot representing a production or aggregated group
 */
export interface CompareSlot {
  id: string;
  type: 'individual' | 'group';
  groupIds: string[];
  label: string;
  color: string;
  dateRangeMode: 'linked' | 'preset' | 'custom';
  datePreset?: DatePreset;
  customDateRange?: { start: Date | null; end: Date | null };
  /** Model codes to filter by for this slot */
  modelCodes: string[];
  /** Granularity for this slot's time series */
  granularity: TimeGranularity;
  /** Whether to use the full production date range */
  useFullGroup: boolean;
  /** Saved date settings for restoring after "Use Production Dates" is disabled (per-slot mode) */
  previousDateSettings?: PreviousDateSettings;
}

/**
 * Complete state for Compare analytics view
 */
export interface CompareState {
  slots: CompareSlot[];
  activeSections: CompareSection[];
  linkedDateRange: boolean;
  linkedGranularity: boolean;
  linkedModelCodes: boolean;
  globalDatePreset: DatePreset;
  globalCustomDateRange: { start: Date | null; end: Date | null };
  globalGranularity: TimeGranularity;
  globalModelCodes: string[];
  enabledMetrics: MetricKey[];
  /** Saved date settings for restoring after "Use Production Dates" is disabled (linked mode) */
  previousDateSettings?: PreviousDateSettings;
}

/**
 * Default compare state factory - creates a new empty slot
 */
export function createEmptyCompareSlot(index: number): CompareSlot {
  return {
    id: `slot-${Date.now()}-${index}`,
    type: 'individual',
    groupIds: [],
    label: '',
    color: COMPARE_SLOT_COLORS[index] || COMPARE_SLOT_COLORS[0],
    dateRangeMode: 'linked',
    modelCodes: [],
    granularity: 'weekly',
    useFullGroup: false,
  };
}

/**
 * Default compare state
 */
export const DEFAULT_COMPARE_STATE: CompareState = {
  slots: [],
  activeSections: ['info', 'kpis', 'chart', 'stats'],
  linkedDateRange: true,
  linkedGranularity: true,
  linkedModelCodes: true,
  globalDatePreset: 'last-30-days',
  globalCustomDateRange: { start: null, end: null },
  globalGranularity: 'weekly',
  globalModelCodes: [],
  enabledMetrics: DEFAULT_METRIC_TOGGLES.filter(m => m.enabled).map(m => m.key),
};

/**
 * Serialized version of CompareSlot for localStorage (dates as ISO strings)
 */
interface SerializedCompareSlot extends Omit<CompareSlot, 'customDateRange'> {
  customDateRange?: { start: string | null; end: string | null };
}

/**
 * Serialized version of CompareState for localStorage (dates as ISO strings)
 */
interface SerializedCompareState extends Omit<CompareState, 'slots' | 'globalCustomDateRange'> {
  slots: SerializedCompareSlot[];
  globalCustomDateRange: { start: string | null; end: string | null };
}

/**
 * Serialize CompareState for localStorage storage
 * Converts Date objects to ISO strings
 */
export function serializeCompareState(state: CompareState): string {
  const serialized: SerializedCompareState = {
    ...state,
    slots: state.slots.map(slot => ({
      ...slot,
      customDateRange: slot.customDateRange ? {
        start: slot.customDateRange.start?.toISOString() || null,
        end: slot.customDateRange.end?.toISOString() || null,
      } : undefined,
    })),
    globalCustomDateRange: {
      start: state.globalCustomDateRange.start?.toISOString() || null,
      end: state.globalCustomDateRange.end?.toISOString() || null,
    },
  };
  return JSON.stringify(serialized);
}

/**
 * Deserialize CompareState from localStorage
 * Converts ISO strings back to Date objects
 */
export function deserializeCompareState(json: string): CompareState | null {
  try {
    const parsed: SerializedCompareState = JSON.parse(json);

    // Validate essential structure
    if (!parsed || !Array.isArray(parsed.slots)) {
      return null;
    }

    return {
      ...parsed,
      slots: parsed.slots.map(slot => ({
        ...slot,
        customDateRange: slot.customDateRange ? {
          start: slot.customDateRange.start ? new Date(slot.customDateRange.start) : null,
          end: slot.customDateRange.end ? new Date(slot.customDateRange.end) : null,
        } : undefined,
      })),
      globalCustomDateRange: {
        start: parsed.globalCustomDateRange.start ? new Date(parsed.globalCustomDateRange.start) : null,
        end: parsed.globalCustomDateRange.end ? new Date(parsed.globalCustomDateRange.end) : null,
      },
    };
  } catch {
    return null;
  }
}
